import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardShell } from "@/components/yc/DashboardShell";
import { api } from "@/lib/api";
import { specLabel } from "@/lib/specialisms";
import { Lock, MapPin } from "lucide-react";

const BrandRoster = () => {
    const [creators, setCreators] = useState([]);
    const [filters, setFilters] = useState({ specialism: "", sector: "", location: "", min_followers: 0 });
    const [specialisms, setSpecialisms] = useState([]);
    const [access, setAccess] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/brand/specialisms").then((r) => setSpecialisms(r.data));
        api.get("/brand/access").then((r) => setAccess(r.data));
        load(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async (f) => {
        try {
            const params = new URLSearchParams(Object.entries(f).filter(([_, v]) => v !== "" && v !== 0));
            const r = await api.get(`/brand/roster?${params.toString()}`);
            setCreators(r.data);
            setError("");
        } catch (e) {
            setError(e.response?.data?.detail || "Could not load roster");
        }
    };

    const apply = () => load(filters);
    const unlocked = access?.unlocked;

    return (
        <DashboardShell role="brand" title="Roster" subtitle="Vetted creators only">
            {access && !unlocked && (
                <div className="yc-card p-6 bg-yc-pink text-white flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="roster-locked-banner">
                    <Lock className="w-8 h-8 shrink-0" />
                    <div className="flex-1">
                        <div className="font-heading font-bold text-xl">Free member view</div>
                        <div className="text-sm">10 featured creators rotate every day — the rest of the roster stays blurred. Names and video showreels fully unlock when you start a campaign or book a taster.</div>
                    </div>
                    <Link to="/brand/membership" data-testid="roster-unlock-cta" className="yc-btn-primary bg-white text-yc-ink text-sm py-2 px-4 shrink-0">Unlock the network →</Link>
                </div>
            )}
            <div className="yc-card-sm p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                <div>
                    <label className="yc-overline">Specialism</label>
                    <select data-testid="roster-specialism" className="yc-input mt-1" value={filters.specialism} onChange={(e) => setFilters({ ...filters, specialism: e.target.value })}>
                        <option value="">Any</option>
                        {specialisms.map((s) => <option key={s} value={s}>{specLabel(s)}</option>)}
                    </select>
                </div>
                <div>
                    <label className="yc-overline">Sector</label>
                    <input data-testid="roster-sector" className="yc-input mt-1" value={filters.sector} onChange={(e) => setFilters({ ...filters, sector: e.target.value })} />
                </div>
                <div>
                    <label className="yc-overline">Location</label>
                    <input data-testid="roster-location" className="yc-input mt-1" value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })} />
                </div>
                <div>
                    <label className="yc-overline">Min followers</label>
                    <input data-testid="roster-followers" type="number" className="yc-input mt-1" value={filters.min_followers} onChange={(e) => setFilters({ ...filters, min_followers: parseInt(e.target.value || 0) })} />
                </div>
                <button data-testid="roster-apply" onClick={apply} className="yc-btn-primary">Apply filters</button>
            </div>

            {error && <div className="yc-card-sm p-4 text-yc-pink">{error}</div>}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {creators.map((c) => {
                    if (c.locked) {
                        // Blurred card — content is decorative and unreadable
                        return (
                            <div key={c.creator_id} className="yc-card p-6 relative overflow-hidden" data-testid={`creator-card-locked-${c.creator_id}`}>
                                <div className="blur-[7px] opacity-60 select-none pointer-events-none" aria-hidden="true">
                                    <div className="flex items-start justify-between">
                                        <h3 className="font-heading font-bold text-xl">Creator Name</h3>
                                        <span className="yc-badge bg-yc-blue text-white">Approved</span>
                                    </div>
                                    <p className="text-sm text-yc-ink/70 mt-2">A vetted creator producing standout work for brands like yours.</p>
                                    <div className="flex flex-wrap gap-1 mt-3">
                                        {(c.specialisms || []).slice(0, 3).map((s) => <span key={s} className="yc-badge bg-white">{specLabel(s)}</span>)}
                                    </div>
                                    <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                                        <div><div className="yc-overline">Quality</div><div className="font-heading font-bold text-lg">{Math.round(c.quality_score || 0)}</div></div>
                                        <div><div className="yc-overline">Reliability</div><div className="font-heading font-bold text-lg">{Math.round(c.reliability_score || 0)}</div></div>
                                        <div><div className="yc-overline">Reach</div><div className="font-heading font-bold text-lg">{c.reach_display}</div></div>
                                    </div>
                                </div>
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                                    <span className="w-10 h-10 rounded-full bg-white border-2 border-yc-ink flex items-center justify-center"><Lock className="w-5 h-5" /></span>
                                    <span className="text-xs font-bold bg-white border-2 border-yc-ink rounded-full px-3 py-1">Unlock with a campaign</span>
                                </div>
                            </div>
                        );
                    }
                    return (
                        <div key={c.creator_id} className="yc-card p-6" data-testid={`creator-card-${c.creator_id}`}>
                            <div className="flex items-start justify-between">
                                <h3 className="font-heading font-bold text-xl" data-testid={`creator-name-${c.creator_id}`}>{c.name || "Creator"}</h3>
                                <div className="flex gap-1">
                                    <span className="yc-badge bg-yc-blue text-white">Approved</span>
                                </div>
                            </div>
                            <p className="text-sm text-yc-ink/70 mt-2 line-clamp-3">{c.bio}</p>
                            <div className="flex flex-wrap gap-1 mt-3">
                                {(c.specialisms || []).map((s) => <span key={s} className="yc-badge bg-white">{specLabel(s)}</span>)}
                            </div>
                            <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                                <div><div className="yc-overline">Quality</div><div className="font-heading font-bold text-lg">{Math.round(c.quality_score || 0)}</div></div>
                                <div><div className="yc-overline">Reliability</div><div className="font-heading font-bold text-lg">{Math.round(c.reliability_score || 0)}</div></div>
                                <div><div className="yc-overline">Reach</div><div className="font-heading font-bold text-lg">{c.reach_display}</div></div>
                            </div>
                            {c.location && <div className="text-xs text-yc-ink/60 mt-3 flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.location}</div>}
                        </div>
                    );
                })}
            </div>
            {creators.length === 0 && (
                <div className="text-center text-yc-ink/60 py-12">No creators match these filters yet.</div>
            )}
        </DashboardShell>
    );
};

export default BrandRoster;
